import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import { useBoolean } from 'react-hanger';
import type { TabWrapperSettings } from 'c-types';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { Alert, TabsWrapper } from 'c-components';
import {
    non_geopath_data,
    geopathData,
} from 'c-main/Components/Campaign/CampaignReportingWrapper/mockData';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';

type Props = {
    id: number | string;
    showRoute: boolean;
    vistarRows: any[];
    geoRows: any[];
    vistarError?: unknown;
    geoError?: unknown;
    isLoading: boolean;
};

/** ---------------- Shared helpers ---------------- */
const parsePercentage = (percentageString: any) => {
    if (percentageString == null) return 0;
    const stringified = String(percentageString);
    const trimmedString = stringified.replace('%', '').trim();
    const parsed = parseFloat(trimmedString);
    return parsed;
};
const normalizeValue = (row: any) => parsePercentage(row['% VAR']) / 100;

const OTHER_GROUP_LABEL = 'Other';
const isBadDspGroupKey = (value: unknown): boolean => {
    const trimmed = String(value ?? '').trim();
    return trimmed === '' || /^["|]+$/.test(trimmed);
};
const normalizeDspGroupKey = (value: unknown): string =>
    isBadDspGroupKey(value) ? OTHER_GROUP_LABEL : String(value ?? '');

const DSP_ADDITIVE_FIELDS = ['Plays', 'DSP Impressions', 'Geopath Impressions'];

const foldDspRowsToOther = (input: any[]): any[] => {
    const byKey = new Map<string, any>();
    input.forEach(row => {
        const owner = normalizeDspGroupKey(row?.['Display Owner']);
        const format = normalizeDspGroupKey(row?.['Display Format']);
        const key = `${owner} ${format}`;
        const existing = byKey.get(key);
        if (!existing) {
            byKey.set(key, { ...row, 'Display Owner': owner, 'Display Format': format });
            return;
        }
        DSP_ADDITIVE_FIELDS.forEach(f => {
            if (f in existing || f in row) {
                existing[f] = (Number(existing[f]) || 0) + (Number(row[f]) || 0);
            }
        });
        const plays = Number(existing.Plays) || 0;
        if ('DSP IM' in existing) {
            existing['DSP IM'] = plays > 0 ? (Number(existing['DSP Impressions']) || 0) / plays : 0;
        }
        if ('Geopath IM' in existing) {
            existing['Geopath IM'] =
                plays > 0 ? (Number(existing['Geopath Impressions']) || 0) / plays : 0;
        }
    });
    return Array.from(byKey.values());
};
const determineColor = (value: number, theme: any) =>
    value === 0 ? 'black' : value > 0 ? theme.palette.success.main : theme.palette.error.main;

const getFilteredCountsExcluding = (
    data: any[],
    filters: Record<string, string[]>,
    filterKeyToExclude: string,
    topBottomFilter: 'top' | 'bottom' | 'none' | null,
) => {
    let filteredDataWithoutSpecificFilter = data.filter(row =>
        Object.entries(filters).every(
            ([key, values]) =>
                key === filterKeyToExclude ||
                (values as string[]).length === 0 ||
                (values as string[]).includes(row[key]),
        ),
    );

    if (topBottomFilter !== 'none' && topBottomFilter != null) {
        filteredDataWithoutSpecificFilter = filteredDataWithoutSpecificFilter.filter(row => {
            const normalizedValue = normalizeValue(row);
            return (
                (topBottomFilter === 'top' && normalizedValue > 0) ||
                (topBottomFilter === 'bottom' && normalizedValue < 0)
            );
        });
    }

    const counts = filteredDataWithoutSpecificFilter.reduce(
        (acc: Record<string, number>, row: any) => {
            const key = row[filterKeyToExclude];
            if (!acc[key]) acc[key] = 0;
            acc[key] += 1;
            return acc;
        },
        {},
    );

    return Object.entries(counts).map(([key, count]) => ({ key, count }));
};

/** ---------------- CheckboxSelect ---------------- */
const CheckboxSelect = ({
    value,
    onChange,
    items,
    itemCounts,
    label,
}: {
    value: string[];
    onChange: (selected: string[]) => void;
    items: string[];
    itemCounts: Record<string, number>;
    label: string;
}) => {
    const handleChange = (event: any) => {
        const {
            target: { value: selectedValues },
        } = event;
        onChange(selectedValues);
    };
    const handleClear = () => onChange([]);

    return (
        <FormControl
            sx={{
                minWidth: '15rem',
                maxWidth: '30rem',
                '& .MuiInputLabel-root': { marginTop: '-0.4rem' },
            }}
        >
            <InputLabel id={`${label}-label`}>{label}</InputLabel>
            <Select
                size="small"
                labelId={`${label}-label`}
                multiple
                value={value}
                onChange={handleChange}
                input={<OutlinedInput label={label} />}
                renderValue={(selected: string[]) => selected.join(', ')}
                IconComponent={value.length !== 0 ? () => null : undefined}
                MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 'fit-content' } } }}
            >
                {items.map(item => (
                    <MenuItem key={item} value={item}>
                        <Checkbox checked={value.indexOf(item) > -1} />
                        <ListItemText primary={`${item} (${itemCounts[item] || 0})`} />
                    </MenuItem>
                ))}
            </Select>
            {value.length > 0 && (
                <IconButton
                    onClick={handleClear}
                    sx={{ position: 'absolute', right: 8, top: 8, p: 0, color: 'text.secondary' }}
                >
                    <ClearIcon />
                </IconButton>
            )}
        </FormControl>
    );
};

/** ---------------- ReportingTable (renders given rows or fetches if none) ---------------- */
interface ReportingTableProps {
    type: 'vistar' | 'geopath';
    id: number | string;
    showRoute: boolean;
    rows?: any[]; // when provided, skip fetching inside
}

const ReportingTable: React.FC<ReportingTableProps> = ({ type, id, showRoute, rows }) => {
    const t = useCommonTranslation();
    const theme = useTheme();

    const [data, setData] = useState<any[]>([]);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [filters, setFilters] = useState<Record<string, string[]>>({
        'Display Owner': [],
        'Display Format': [],
    });
    const [originalRowsPerPage, setOriginalRowsPerPage] = useState(25);
    const [uniqueDisplayOwners, setUniqueDisplayOwners] = useState<string[]>([]);
    const [uniqueDisplayFormats, setUniqueDisplayFormats] = useState<string[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [topBottomFilter, setTopBottomFilter] = useState<'top' | 'bottom' | 'none' | null>(null);
    const [topBottomCount, setTopBottomCount] = useState(5);
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<string>('DSP Impressions');
    const topBottomToggled = useBoolean(false);

    const { start } = useAPIClientRequest(ApiClient.Reports.impressionMultipliers);
    const { start: geoStart } = useAPIClientRequest(ApiClient.Reports.geopathImpressionMultipliers);
    const { environmentMode } = useCampaignWrapperState(Number(id));

    const hydrateFromRows = useCallback((input: any[]) => {
        const folded = foldDspRowsToOther(input);
        setData(folded);
        setFilteredData(folded);
        const displayOwners = [...new Set(folded.map(row => row['Display Owner']))];
        const displayFormats = [...new Set(folded.map(row => row['Display Format']))];
        setUniqueDisplayOwners(displayOwners);
        setUniqueDisplayFormats(displayFormats);
    }, []);

    // If external rows provided, use them & skip fetching
    useEffect(() => {
        if (rows && Array.isArray(rows)) hydrateFromRows(rows);
    }, [rows, hydrateFromRows]);

    const getData = useCallback(async () => {
        if (rows && Array.isArray(rows)) return;

        if (type === 'geopath' && id === 9464) {
            const formattedData = geopathData.map(row => ({
                ...row,
                '% VAR': parsePercentage(row['% VAR']),
            }));
            hydrateFromRows(formattedData);
            return;
        }
        if (type === 'vistar' && id === 9464) {
            const formattedData = non_geopath_data.map(row => ({
                ...row,
                '% VAR': parsePercentage(row['% VAR']),
            }));
            hydrateFromRows(formattedData);
            return;
        }
        if (type === 'vistar') {
            const response = await start({ id, environment: environmentMode });
            if (response?.data) {
                const dataReformatted = (response as any).data.map((row: any) => ({
                    'Display Owner': row.display_owner,
                    'Display Format': row.display_format,
                    Plays: row.sum_of_play,
                    'DSP Impressions': row.dsp_impressions,
                    'DSP IM': row.dsp_avg_impressions,
                    'Average IM': row.average_im,
                    'IM VAR': row.variance,
                    '% VAR': row.variance_percent * 100,
                }));
                hydrateFromRows(dataReformatted);
            }
        }
        if (type === 'geopath') {
            const response = await geoStart({ id, environment: environmentMode });
            if (response?.data) {
                const formattedData = (response as any).data.map((row: any) => ({
                    'Display Owner': row.display_owner,
                    'Display Format': row.display_format,
                    Plays: row.sum_of_play,
                    'DSP Impressions': row.dsp_impressions,
                    'Geopath Impressions': row.geopath_impressions,
                    VAR: row.variance,
                    'DSP IM': row.dsp_avg_impressions,
                    'Geopath IM': row.geopath_avg_impressions,
                    'IM VAR': row.im_variance,
                    '% VAR': row.variance_percent * 100,
                }));
                hydrateFromRows(formattedData);
            }
        }
    }, [rows, type, id, hydrateFromRows, start, environmentMode, geoStart]);

    useEffect(() => {
        getData();
    }, [getData]);

    useEffect(() => {
        if (topBottomFilter === 'none') setTopBottomFilter(null);
    }, [topBottomFilter]);

    // Filtering, top/bottom, sorting pipeline (unchanged except using current state)
    useEffect(() => {
        let tempData = data.filter(row =>
            Object.entries(filters).every(
                ([key, values]) => values.length === 0 || values.includes(row[key]),
            ),
        );

        if (topBottomFilter !== 'none' && topBottomFilter != null) {
            tempData = tempData
                .filter(row => {
                    const normalizedValue = normalizeValue(row);
                    return (
                        (topBottomFilter === 'top' && normalizedValue > 0) ||
                        (topBottomFilter === 'bottom' && normalizedValue < 0)
                    );
                })
                .sort(
                    (a, b) => parseFloat(b['DSP Impressions']) - parseFloat(a['DSP Impressions']),
                );

            if (topBottomToggled.value) {
                setTopBottomCount(tempData.length + 1);
                setRowsPerPage(tempData.length + 1);
                tempData = tempData.slice(0, topBottomCount);
            } else {
                tempData = tempData.slice(0, topBottomCount);
            }
            topBottomToggled.setFalse();
        } else {
            setRowsPerPage(originalRowsPerPage);
        }

        if (orderBy) {
            tempData.sort((a, b) => {
                if (orderBy === 'Variance') {
                    const valueA = parsePercentage(a['% VAR']) / 100;
                    const valueB = parsePercentage(b['% VAR']) / 100;
                    return order === 'asc' ? valueA - valueB : valueB - valueA;
                }
                if (orderBy === 'Display Owner' || orderBy === 'Display Format') {
                    const valueA = String(a[orderBy]).toLowerCase();
                    const valueB = String(b[orderBy]).toLowerCase();
                    if (order === 'asc') return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
                    return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
                }
                const valueA = parseFloat(a[orderBy]);
                const valueB = parseFloat(b[orderBy]);
                if (order === 'asc') return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
                return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
            });
        }

        setFilteredData(tempData);
    }, [
        filters,
        data,
        topBottomFilter,
        topBottomCount,
        order,
        orderBy,
        topBottomToggled,
        originalRowsPerPage,
    ]);

    const handleFilterChange = (event: any, field: string) => {
        setFilters(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleTopBottomFilterChange = (event: any) => {
        const newFilter = event.target.value as 'top' | 'bottom' | 'none' | null;

        if (newFilter !== 'none' && newFilter != null) {
            setFilters({ 'Display Owner': [], 'Display Format': [] });
        }

        setTopBottomFilter(newFilter);

        if (newFilter !== 'none' && newFilter != null) {
            setOriginalRowsPerPage(rowsPerPage);
        }

        topBottomToggled.setTrue();
    };

    const handleTopBottomCountChange = (event: any) => {
        const newCount = parseInt(event.target.value, 10);
        setTopBottomCount(newCount);
        setRowsPerPage(newCount);
        setPage(0);
    };

    const handleChangePage = (_: any, newPage: number) => setPage(newPage);
    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const headers = {
        'Display Owner': t('Modules.Main.Campaigns.Overview.dsp.displayOwner'),
        'Display Format': t('Modules.Main.Campaigns.Overview.dsp.displayFormat'),
        Plays: t('Modules.Main.Campaigns.Overview.dsp.plays'),
        'DSP Impressions': t('Modules.Main.Campaigns.Overview.dsp.dspImpressions'),
        'Geopath Impressions': t(
            showRoute
                ? 'Modules.Main.Campaigns.Overview.dsp.routeImpressions'
                : 'Modules.Main.Campaigns.Overview.dsp.geopathImpressions',
        ),
        'DSP IM': t('Modules.Main.Campaigns.Overview.dsp.dspIm'),
        'Average IM': t('Modules.Main.Campaigns.Overview.dsp.averageFormatIm'),
        'Geopath IM': t(
            showRoute
                ? 'Modules.Main.Campaigns.Overview.dsp.routeIm'
                : 'Modules.Main.Campaigns.Overview.dsp.geopathIm',
        ),
        Variance: t('Modules.Main.Campaigns.Overview.dsp.var'),
        VAR: t('Modules.Main.Campaigns.Overview.dsp.var2'),
        'IM VAR': t('Modules.Main.Campaigns.Overview.dsp.imVar'),
    };

    const displayedHeaders =
        type === 'geopath'
            ? {
                  'Display Owner': headers['Display Owner'],
                  'Display Format': headers['Display Format'],
                  Plays: headers.Plays,
                  'DSP Impressions': headers['DSP Impressions'],
                  'Geopath Impressions': headers['Geopath Impressions'],
                  VAR: headers.VAR,
                  'DSP IM': headers['DSP IM'],
                  'Geopath IM': headers['Geopath IM'],
                  'IM VAR': headers['IM VAR'],
                  Variance: headers.Variance,
              }
            : {
                  'Display Owner': headers['Display Owner'],
                  'Display Format': headers['Display Format'],
                  Plays: headers.Plays,
                  'DSP Impressions': headers['DSP Impressions'],
                  'DSP IM': headers['DSP IM'],
                  'Average IM': headers['Average IM'],
                  'IM VAR': headers['IM VAR'],
                  Variance: headers.Variance,
              };

    const rowsPerPageOptions = useMemo(() => {
        const options = [5, 10, 25, data.length];
        if (rowsPerPage <= data.length) options.push(rowsPerPage);
        const uniqueOptions = [...new Set(options.filter(v => v <= data.length))];
        return uniqueOptions.sort((a, b) => a - b);
    }, [rowsPerPage, data.length]);

    const displayOwnerCounts = useMemo(() => {
        const counts = getFilteredCountsExcluding(data, filters, 'Display Owner', topBottomFilter);
        return uniqueDisplayOwners.reduce((acc: Record<string, number>, owner) => {
            const count = counts.find(entry => entry.key === owner)?.count || 0;
            acc[owner] = count as number;
            return acc;
        }, {});
    }, [data, filters, topBottomFilter, uniqueDisplayOwners]);

    const displayFormatCounts = useMemo(() => {
        const counts = getFilteredCountsExcluding(data, filters, 'Display Format', topBottomFilter);
        return uniqueDisplayFormats.reduce((acc: Record<string, number>, format) => {
            const count = counts.find(entry => entry.key === format)?.count || 0;
            acc[format] = count as number;
            return acc;
        }, {});
    }, [data, filters, topBottomFilter, uniqueDisplayFormats]);

    return (
        <Stack gap={2}>
            <Box display="flex" gap={2} alignItems="center">
                <FormControl>
                    {!topBottomFilter && (
                        <InputLabel
                            sx={{
                                transform: 'translateY(0.6rem)',
                                ml: '1rem',
                                '&.MuiInputLabel-shrink': {
                                    transform: 'translateY(0.6rem)',
                                    ml: '1rem',
                                },
                            }}
                        >
                            {t('Modules.Main.Campaigns.Overview.dsp.value')}
                        </InputLabel>
                    )}
                    <Select
                        value={topBottomFilter}
                        onChange={handleTopBottomFilterChange}
                        displayEmpty
                        size="small"
                        IconComponent={topBottomFilter ? () => null : undefined}
                        sx={{ minWidth: '15rem' }}
                    >
                        {topBottomFilter !== null || topBottomFilter === 'none' ? (
                            <MenuItem value="none">
                                {t('Modules.Main.Campaigns.Overview.dsp.value')}
                            </MenuItem>
                        ) : null}
                        <MenuItem value="top">
                            {t('Modules.Main.Campaigns.Overview.dsp.top')}
                        </MenuItem>
                        <MenuItem value="bottom">
                            {t('Modules.Main.Campaigns.Overview.dsp.bottom')}
                        </MenuItem>
                    </Select>
                </FormControl>

                {topBottomFilter !== 'none' && topBottomFilter != null && (
                    <TextField
                        type="number"
                        value={topBottomCount}
                        onChange={handleTopBottomCountChange}
                        size="small"
                        InputProps={{ inputProps: { min: 1 } }}
                        label={t('Modules.Main.Campaigns.Overview.dsp.count')}
                        sx={{ flexShrink: 0 }}
                    />
                )}

                <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                    <CheckboxSelect
                        value={filters['Display Owner']}
                        onChange={selectedValues =>
                            handleFilterChange(
                                { target: { value: selectedValues } },
                                'Display Owner',
                            )
                        }
                        items={uniqueDisplayOwners}
                        itemCounts={displayOwnerCounts}
                        label={t('Modules.Main.Campaigns.Overview.dsp.displayOwner')}
                    />
                    <CheckboxSelect
                        value={filters['Display Format']}
                        onChange={selectedValues =>
                            handleFilterChange(
                                { target: { value: selectedValues } },
                                'Display Format',
                            )
                        }
                        items={uniqueDisplayFormats}
                        itemCounts={displayFormatCounts}
                        label={t('Modules.Main.Campaigns.Overview.dsp.displayFormat')}
                    />
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                            {Object.keys(displayedHeaders).map((header, index) => (
                                <TableCell
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    sortDirection={orderBy === header ? order : 'asc'}
                                >
                                    <TableSortLabel
                                        active={orderBy === header}
                                        direction={orderBy === header ? order : 'asc'}
                                        onClick={() => handleRequestSort(header)}
                                        sx={{
                                            color: theme.palette.custom.contentBgColor,
                                            '&.MuiTableSortLabel-root:hover': {
                                                color: theme.palette.custom.contentBgColor,
                                            },
                                            '&.Mui-active': {
                                                color: theme.palette.custom.contentBgColor,
                                            },
                                            '& .MuiTableSortLabel-icon': {
                                                color: `${theme.palette.custom.contentBgColor} !important`,
                                            },
                                        }}
                                    >
                                        <Typography color={theme.palette.custom.contentBgColor}>
                                            {headers[header as keyof typeof headers]}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredData
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, rowIndex) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={rowIndex}>
                                    <TableCell>{row['Display Owner']}</TableCell>
                                    <TableCell>{row['Display Format']}</TableCell>
                                    <TableCell>{Math.round(row.Plays).toLocaleString()}</TableCell>
                                    <TableCell>
                                        {Math.round(row['DSP Impressions']).toLocaleString()}
                                    </TableCell>

                                    {type === 'geopath' && (
                                        <TableCell>
                                            {Math.round(
                                                row['Geopath Impressions'],
                                            ).toLocaleString()}
                                        </TableCell>
                                    )}

                                    {type === 'geopath' && (
                                        <TableCell>
                                            <Typography
                                                color={
                                                    Math.round(parseFloat(row.VAR)) === 0
                                                        ? 'black'
                                                        : Math.round(parseFloat(row.VAR)) > 0
                                                        ? theme.palette.success.main
                                                        : theme.palette.error.main
                                                }
                                            >
                                                {Math.round(parseFloat(row.VAR)).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                    )}

                                    <TableCell>
                                        {parseFloat(row['DSP IM']).toFixed(2).toLocaleString()}
                                    </TableCell>

                                    {type === 'geopath' && (
                                        <TableCell>
                                            {parseFloat(row['Geopath IM'])
                                                .toFixed(2)
                                                .toLocaleString()}
                                        </TableCell>
                                    )}

                                    {type === 'vistar' && (
                                        <TableCell>
                                            {parseFloat(row['Average IM'])
                                                .toFixed(2)
                                                .toLocaleString()}
                                        </TableCell>
                                    )}

                                    <TableCell>
                                        <Typography
                                            color={
                                                parseFloat(row['IM VAR']) === 0
                                                    ? 'black'
                                                    : parseFloat(row['IM VAR']) > 0
                                                    ? theme.palette.success.main
                                                    : theme.palette.error.main
                                            }
                                        >
                                            {String(
                                                parseFloat(row['IM VAR'])
                                                    .toFixed(2)
                                                    .toLocaleString(),
                                            )}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography
                                            color={determineColor(normalizeValue(row), theme)}
                                        >
                                            {`${parseFloat(row['% VAR']).toFixed(0)} %`}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Stack>
    );
};

/** ---------------- DspReporting (fetch here & pass rows; hide empty tabs) ---------------- */
const DspReporting: React.FC<Props> = ({
    id,
    showRoute,
    vistarRows,
    geoRows,
    vistarError,
    geoError,
    isLoading,
}) => {
    const tabs = useMemo<TabWrapperSettings['tabs']>(() => {
        const t: TabWrapperSettings['tabs'] = [];

        if (Array.isArray(vistarRows) && vistarRows.length > 0) {
            t.push({
                title: showRoute
                    ? 'Modules.Main.Campaigns.Overview.dsp.routeVistar'
                    : 'Modules.Main.Campaigns.Overview.dsp.vistar',
                content: vistarError ? (
                    <Alert severity="error">{String(vistarError)}</Alert>
                ) : (
                    <ReportingTable type="vistar" id={id} showRoute={showRoute} rows={vistarRows} />
                ),
                tabPanelStyling: { py: 0 },
                className: 'vistar-Table',
            });
        }

        if (Array.isArray(geoRows) && geoRows.length > 0) {
            t.push({
                title: showRoute
                    ? 'Modules.Main.Campaigns.Overview.dsp.route'
                    : 'Modules.Main.Campaigns.Overview.dsp.geopath',
                content: geoError ? (
                    <Alert severity="error">{String(geoError)}</Alert>
                ) : (
                    <ReportingTable type="geopath" id={id} showRoute={showRoute} rows={geoRows} />
                ),
                tabPanelStyling: { py: 0 },
                className: 'geopath-Table',
            });
        }

        return t;
    }, [vistarRows, geoRows, showRoute, vistarError, id, geoError]);

    if (isLoading) {
        return (
            <Box py={2} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    if (!tabs.length) return null;
    return <TabsWrapper tabs={tabs} type="secondary" />;
};

export default DspReporting;
